/**
 * InvoiceDetailPage (11F.7) — full-page invoice view at `/invoices/:id`.
 *
 * Reference: `Invoices and payments-detail.png` for information
 * architecture; visual language is Candeza's own (D13).
 *
 * Layout: the rendered invoice document on the left (what the customer
 * receives), operational context on the right rail (payments, credit notes,
 * internal note, activity). That split is deliberate — the left column is
 * the financial record and the right is everything staff do *about* it.
 *
 * Two decisions worth stating:
 *
 * 1. **Activity log is derived, not read from `audit_log`.** The audit
 *    middleware sets `entity_id` to the last URL segment, so
 *    `POST /invoices/{id}/void` is recorded against entity_id `"void"` —
 *    the collection cannot be filtered per invoice. Rather than show a
 *    misleading feed, the timeline below is reconstructed from the
 *    invoice's own fields plus its payments and refunds. That is exact by
 *    construction and cannot drift. See docs/04-modules/billing.md.
 *
 * 2. **No delete.** An issued invoice is voided with a mandatory reason
 *    (invariant I-4). `paid` invoices cannot even be voided — the backend
 *    refuses, so the action is hidden rather than offered-then-rejected.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';

import {
  ActionMenu,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dropdown,
  Input,
  Modal,
} from '@/components/common';
import { PageContainer } from '@/components/layout';
import { ApiError } from '@core/api/client';
import {
  fetchInvoicePdf,
  useInvoice,
  useIssueInvoice,
  usePayments,
  usePaymentRefunds,
  useRecordPayment,
  useVoidInvoice,
  type InvoiceResponse,
  type PaymentMethod,
  type PaymentResponse,
} from '@features/invoices/api/billing';
import {
  INVOICE_ISSUABLE_STATUSES,
  INVOICE_PAYABLE_STATUSES,
  INVOICE_VOIDABLE_STATUSES,
  PAYMENT_METHOD_OPTIONS,
  formatMinorAmount,
  invoiceStatusLabel,
  paymentMethodLabel,
} from '@features/invoices/config/ui';

// ── Layout ────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: ${({ theme }) => theme.spacing.lg};
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const FieldLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
  color: ${({ theme }) => theme.colors.textTertiary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
`;

const FieldValue = styled.div<{ $emphasis?: boolean }>`
  margin-top: 2px;
  font-size: ${({ theme, $emphasis }) =>
    $emphasis ? theme.typography.fontSizes.xl : theme.typography.fontSizes.sm};
  font-weight: ${({ theme, $emphasis }) =>
    $emphasis ? theme.typography.fontWeights.bold : theme.typography.fontWeights.medium};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text};
`;

const LineTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  th {
    text-align: left;
    padding: ${({ theme }) => theme.spacing.sm} 0;
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
    text-transform: uppercase;
    letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
    color: ${({ theme }) => theme.colors.textTertiary};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    white-space: nowrap;
  }
  td {
    padding: ${({ theme }) => theme.spacing.sm} 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    vertical-align: top;
  }
  th.num,
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Totals = styled.dl`
  margin: ${({ theme }) => theme.spacing.md} 0 0;
  margin-left: auto;
  max-width: 320px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  dt {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  dd {
    margin: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  dt.grand,
  dd.grand {
    padding-top: 6px;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
    font-size: ${({ theme }) => theme.typography.fontSizes.md};
  }
`;

const RailTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const Empty = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const TimelineList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;

  li {
    position: relative;
    padding: 0 0 ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  }
  /* Connector rail + node — encodes chronology, not decoration. */
  li:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 14px;
    bottom: 0;
    width: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
  li::after {
    content: '';
    position: absolute;
    left: 0;
    top: 5px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.surface};
    border: 2px solid ${({ theme }) => theme.colors.primary};
  }
`;

const TimelineWhen = styled.div`
  color: ${({ theme }) => theme.colors.textTertiary};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  margin-top: 1px;
`;

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface TimelineEvent {
  at: string;
  title: string;
  detail?: string;
}

/**
 * Reconstruct the invoice's history from the documents themselves.
 * See the file docstring for why this is not read from `audit_log`.
 */
function buildTimeline(
  invoice: InvoiceResponse,
  payments: PaymentResponse[],
): TimelineEvent[] {
  const events: TimelineEvent[] = [
    { at: invoice.created_at, title: 'Invoice created' },
  ];

  // The number is allocated exactly once, at issue time — its presence is
  // the only durable evidence the invoice was issued (there is no
  // `issued_at` field on the document).
  if (invoice.number && invoice.status !== 'draft') {
    events.push({
      at: invoice.updated_at,
      title: `Issued as ${invoice.number}`,
    });
  }

  for (const p of payments) {
    if (p.status === 'voided') {
      events.push({
        at: p.voided_at ?? p.updated_at,
        title: `Payment of ${formatMinorAmount(p.amount)} voided`,
        detail: p.voided_reason ?? undefined,
      });
    }
    events.push({
      at: p.recorded_at,
      title: `Payment of ${formatMinorAmount(p.amount)} recorded`,
      detail: `${paymentMethodLabel(p.method)}${
        p.reference_number ? ` · ${p.reference_number}` : ''
      }`,
    });
    if ((p.refunded_amount ?? 0) > 0) {
      events.push({
        at: p.updated_at,
        title: `${formatMinorAmount(p.refunded_amount ?? 0)} refunded`,
        detail: 'Payment remains valid — money returned to the payer.',
      });
    }
  }

  if (invoice.voided_at) {
    events.push({
      at: invoice.voided_at,
      title: 'Invoice voided',
      detail: invoice.voided_reason ?? undefined,
    });
  }

  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

// ── Record-payment modal ──────────────────────────────────────────────────

const RecordPaymentModal: React.FC<{
  invoice: InvoiceResponse;
  isOpen: boolean;
  onClose: () => void;
}> = ({ invoice, isOpen, onClose }) => {
  const recordPayment = useRecordPayment();
  // Pre-fill with the full outstanding balance — the overwhelmingly common
  // case, and the backend rejects anything above it anyway.
  const [amount, setAmount] = useState(() => (invoice.amount_due / 100).toFixed(2));
  const [method, setMethod] = useState<PaymentMethod>('upi');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const submit = async () => {
    const paise = Math.round(parseFloat(amount || '0') * 100);
    if (!Number.isFinite(paise) || paise <= 0) {
      toast.error('Enter an amount greater than zero.');
      return;
    }
    try {
      await recordPayment.mutateAsync({
        invoice_id: invoice.id,
        amount: paise,
        payment_date: date,
        method,
        reference_number: reference || undefined,
      });
      toast.success('Payment recorded.');
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not record the payment.',
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record payment"
      size="sm"
      data-testid="candeza-invoice-record-payment-modal"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input
          label="Amount (₹)"
          type="number"
          step="0.01"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={`Outstanding: ${formatMinorAmount(invoice.amount_due, invoice.currency)}`}
          data-testid="candeza-invoice-payment-amount"
        />
        <Input
          label="Payment date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="candeza-invoice-payment-date"
        />
        <Dropdown
          label="Method"
          options={PAYMENT_METHOD_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={method}
          onChange={(v) => setMethod(v as PaymentMethod)}
          testId="candeza-invoice-payment-method"
        />
        <Input
          label="Reference / receipt no. (optional)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          data-testid="candeza-invoice-payment-reference"
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={onClose} disabled={recordPayment.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={recordPayment.isPending}
            data-testid="candeza-invoice-payment-submit"
          >
            {recordPayment.isPending ? 'Recording…' : 'Record payment'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ── Void modal (reason is mandatory) ──────────────────────────────────────

const VoidInvoiceModal: React.FC<{
  invoice: InvoiceResponse;
  isOpen: boolean;
  onClose: () => void;
}> = ({ invoice, isOpen, onClose }) => {
  const voidInvoice = useVoidInvoice();
  const [reason, setReason] = useState('');

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required to void an invoice.');
      return;
    }
    try {
      await voidInvoice.mutateAsync({ id: invoice.id, body: { reason: reason.trim() } });
      toast.success('Invoice voided.');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not void the invoice.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Void invoice"
      size="sm"
      data-testid="candeza-invoice-void-modal"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 14, color: '#475569' }}>
          Voiding reverses this invoice on the ledger. The record is kept —
          invoices are never deleted — and the reason below is stored with it.
        </p>
        <Input
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Raised against the wrong student"
          data-testid="candeza-invoice-void-reason"
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={onClose} disabled={voidInvoice.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            disabled={voidInvoice.isPending || !reason.trim()}
            data-testid="candeza-invoice-void-submit"
          >
            {voidInvoice.isPending ? 'Voiding…' : 'Void invoice'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────

export const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: invoice, isLoading, isError } = useInvoice(id);
  const { data: paymentsResult } = usePayments({ invoiceId: id, limit: 100 });
  const issueInvoice = useIssueInvoice();

  const [payOpen, setPayOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [issueConfirm, setIssueConfirm] = useState(false);

  const payments = useMemo(
    () => (paymentsResult?.items ?? []).filter((p) => p.status !== 'voided'),
    [paymentsResult],
  );
  // The timeline intentionally includes VOIDED payments — a void is part of
  // the invoice's history and hiding it would make the ledger look wrong.
  // The rail above shows only active ones.
  const timeline = useMemo(
    () => (invoice ? buildTimeline(invoice, paymentsResult?.items ?? []) : []),
    [invoice, paymentsResult],
  );

  if (isLoading) {
    return (
      <PageContainer title="Loading">
        <Card padding="lg">
          <p style={{ color: '#64748b' }}>Loading invoice…</p>
        </Card>
      </PageContainer>
    );
  }

  if (isError || !invoice) {
    return (
      <PageContainer title="Invoice not found">
        <Card padding="lg">
          <p>This invoice does not exist, or you do not have access to it.</p>
          <Button variant="outline" onClick={() => navigate('/invoices')}>
            Back to invoices
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const canIssue = INVOICE_ISSUABLE_STATUSES.has(invoice.status);
  const canPay = INVOICE_PAYABLE_STATUSES.has(invoice.status);
  const canVoid = INVOICE_VOIDABLE_STATUSES.has(invoice.status);

  const actions = [
    ...(canIssue
      ? [{ key: 'issue', label: 'Issue invoice', onSelect: () => setIssueConfirm(true) }]
      : []),
    ...(canPay
      ? [{ key: 'pay', label: 'Record payment', onSelect: () => setPayOpen(true) }]
      : []),
    {
      key: 'pdf',
      label: 'Download PDF',
      onSelect: async () => {
        try {
          const blob = await fetchInvoicePdf(invoice.id);
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener');
          // Revoke on the next tick — revoking synchronously races the
          // new tab's fetch of the blob and yields a blank viewer.
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
          toast.error('Could not generate the PDF.');
        }
      },
    },
    {
      key: 'credit-note',
      label: 'Issue credit note',
      // Phase 16 (GST invoices + credit notes) has not shipped — there is
      // no credit-note schema, endpoint or service anywhere in the backend
      // (verified 11F.7). Shown disabled rather than hidden so the gap is
      // visible to the operator instead of silently absent.
      disabled: true,
      onSelect: () => undefined,
    },
    ...(canVoid
      ? [
          {
            key: 'void',
            label: 'Void invoice',
            destructive: true,
            onSelect: () => setVoidOpen(true),
          },
        ]
      : []),
  ];

  return (
    <PageContainer
      title={invoice.number ? `Invoice ${invoice.number}` : 'Draft invoice'}
      subtitle={invoice.student_name || 'Unknown student'}
      data-testid="candeza-invoice-detail-page"
      actions={
        <ActionMenu
          items={actions}
          triggerLabel="Invoice actions"
          testId="candeza-invoice-actions"
        />
      }
    >
      <Grid>
        {/* ── The invoice document ─────────────────────────────────── */}
        <Card padding="lg" data-testid="candeza-invoice-document">
          <SummaryStrip>
            <div>
              <FieldLabel>Amount due</FieldLabel>
              <FieldValue $emphasis>
                {formatMinorAmount(invoice.amount_due, invoice.currency)}
              </FieldValue>
            </div>
            <div>
              <FieldLabel>Status</FieldLabel>
              <FieldValue>
                <Badge variant="default" dot>
                  {invoiceStatusLabel(invoice.status)}
                </Badge>
              </FieldValue>
            </div>
            <div>
              <FieldLabel>Issued</FieldLabel>
              <FieldValue>{fmtDate(invoice.issue_date)}</FieldValue>
            </div>
            <div>
              <FieldLabel>Due</FieldLabel>
              <FieldValue>{fmtDate(invoice.due_date)}</FieldValue>
            </div>
          </SummaryStrip>

          <div style={{ marginBottom: 16 }}>
            <FieldLabel>Billed to</FieldLabel>
            <FieldValue data-testid="candeza-invoice-invoicee">
              {invoice.student_name || 'Unknown student'}
            </FieldValue>
          </div>

          <TableScroll>
            <LineTable data-testid="candeza-invoice-line-items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th className="num">Unit</th>
                  <th className="num">Tax</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items.map((li, idx) => (
                  <tr key={`${li.description}-${idx}`}>
                    <td>
                      {/* Subject NAME is the display source of truth (D2 / */}
                      {/* 11E.4); description is the fallback for ad-hoc fees */}
                      {/* and for lines whose subject no longer resolves. */}
                      <div style={{ fontWeight: 500 }}>
                        {li.subject_name || li.description}
                      </div>
                      {li.subject_name && li.description !== li.subject_name && (
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>
                          {li.description}
                        </div>
                      )}
                    </td>
                    <td className="num">{li.quantity}</td>
                    <td className="num">
                      {formatMinorAmount(li.unit_price, invoice.currency)}
                    </td>
                    <td className="num">
                      {li.tax_rate ? `${(li.tax_rate * 100).toFixed(0)}%` : '—'}
                    </td>
                    <td className="num">
                      {formatMinorAmount(li.line_total, invoice.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </LineTable>
          </TableScroll>

          <Totals>
            <dt>Subtotal</dt>
            <dd>{formatMinorAmount(invoice.subtotal, invoice.currency)}</dd>
            <dt>Tax</dt>
            <dd>{formatMinorAmount(invoice.tax_total, invoice.currency)}</dd>
            <dt className="grand">Total</dt>
            <dd className="grand">{formatMinorAmount(invoice.total, invoice.currency)}</dd>
            <dt>Paid</dt>
            <dd>−{formatMinorAmount(invoice.amount_paid, invoice.currency)}</dd>
            <dt className="grand">Balance</dt>
            <dd className="grand">
              {formatMinorAmount(invoice.amount_due, invoice.currency)}
            </dd>
          </Totals>

          {invoice.notes && (
            <div style={{ marginTop: 20 }}>
              <FieldLabel>Notes</FieldLabel>
              <p style={{ fontSize: 14, color: '#334155', marginTop: 4 }}>
                {invoice.notes}
              </p>
            </div>
          )}
        </Card>

        {/* ── Operational rail ─────────────────────────────────────── */}
        <Rail>
          <Card padding="lg" data-testid="candeza-invoice-payments">
            <RailTitle>Payments</RailTitle>
            {payments.length === 0 ? (
              <Empty>No payments recorded yet.</Empty>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {payments.map((p) => (
                  <PaymentRow key={p.id} payment={p} currency={invoice.currency} />
                ))}
              </div>
            )}
          </Card>

          <Card padding="lg" data-testid="candeza-invoice-credit-notes">
            <RailTitle>Credit notes</RailTitle>
            <Empty>
              Credit notes arrive with GST invoicing (Phase 16). Until then, use
              a refund against the payment, or void the invoice if it was raised
              in error.
            </Empty>
          </Card>

          <Card padding="lg" data-testid="candeza-invoice-activity">
            <RailTitle>Activity</RailTitle>
            <TimelineList>
              {timeline.map((e, i) => (
                <li key={`${e.at}-${i}`}>
                  <div>{e.title}</div>
                  {e.detail && (
                    <div style={{ color: '#64748b', fontSize: 13 }}>{e.detail}</div>
                  )}
                  <TimelineWhen>{fmtDateTime(e.at)}</TimelineWhen>
                </li>
              ))}
            </TimelineList>
          </Card>
        </Rail>
      </Grid>

      <RecordPaymentModal
        invoice={invoice}
        isOpen={payOpen}
        onClose={() => setPayOpen(false)}
      />
      <VoidInvoiceModal
        invoice={invoice}
        isOpen={voidOpen}
        onClose={() => setVoidOpen(false)}
      />
      <ConfirmDialog
        isOpen={issueConfirm}
        onClose={() => setIssueConfirm(false)}
        onConfirm={async () => {
          try {
            await issueInvoice.mutateAsync(invoice.id);
            toast.success('Invoice issued.');
          } catch (err) {
            toast.error(
              err instanceof ApiError ? err.message : 'Could not issue the invoice.',
            );
          } finally {
            setIssueConfirm(false);
          }
        }}
        title="Issue this invoice?"
        description="Issuing allocates the next invoice number and posts the charge to the student's ledger. This cannot be undone — an issued invoice can only be voided."
        confirmLabel="Issue invoice"
        busy={issueInvoice.isPending}
        testIdPrefix="candeza-invoice-issue-confirm"
      />
    </PageContainer>
  );
};

/** One payment in the rail, with its refund roll-up when there is one. */
const PaymentRow: React.FC<{ payment: PaymentResponse; currency: string }> = ({
  payment,
  currency,
}) => {
  const { data: refunds } = usePaymentRefunds(
    (payment.refunded_amount ?? 0) > 0 ? payment.id : undefined,
  );

  return (
    <div
      style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}
      data-testid={`candeza-invoice-payment-${payment.id}`}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMinorAmount(payment.amount, currency)}
        </strong>
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {paymentMethodLabel(payment.method)}
        </span>
      </div>
      <div style={{ fontSize: 12, color: '#64748b' }}>
        {fmtDate(payment.payment_date)}
        {payment.reference_number ? ` · ${payment.reference_number}` : ''}
      </div>
      {(payment.refunded_amount ?? 0) > 0 && (
        <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
          {formatMinorAmount(payment.refunded_amount ?? 0, currency)} refunded
          {refunds?.items?.[0]?.reason ? ` — ${refunds.items[0].reason}` : ''}
        </div>
      )}
    </div>
  );
};

export default InvoiceDetailPage;
